import React, { useRef, useMemo, useEffect } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

// Shared geometries - created once, reused
const sharedCircleGeometry = new THREE.CircleGeometry(1, 6);
const sharedPlaneGeometry = new THREE.PlaneGeometry(1, 1);

// Temp objects for matrix calculations
const tempMatrix = new THREE.Matrix4();
const tempPosition = new THREE.Vector3();
const tempScale = new THREE.Vector3();
const tempQuaternion = new THREE.Quaternion();

const BlockPlaceEffect = ({ position, color, onComplete }) => {
  const groupRef = useRef();
  const flashRef = useRef();
  const sparkleInstanceRef = useRef();
  const startTime = useRef(Date.now());

  const sparkleCount = 12;

  // Pre-calculate sparkle data
  const sparkleData = useMemo(() => {
    const data = [];
    for (let i = 0; i < sparkleCount; i++) {
      const angle = (i / sparkleCount) * Math.PI * 2;
      data.push({
        angle,
        speed: 2 + Math.random() * 2,
        size: 0.1 + Math.random() * 0.1,
      });
    }
    return data;
  }, []);

  const baseColor = useMemo(() => new THREE.Color(color), [color]);

  // Create instanced material
  const sparkleMaterial = useMemo(() => new THREE.MeshBasicMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 1,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  }), []);

  // Initialize instance matrices
  useEffect(() => {
    if (sparkleInstanceRef.current) {
      sparkleData.forEach((data, i) => {
        tempPosition.set(0, 0, 0);
        tempScale.setScalar(data.size);
        tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
        sparkleInstanceRef.current.setMatrixAt(i, tempMatrix);
      });
      sparkleInstanceRef.current.instanceMatrix.needsUpdate = true;
    }
  }, [sparkleData]);

  // Single consolidated useFrame for all animations
  useFrame(() => {
    if (!groupRef.current) return;

    const elapsed = (Date.now() - startTime.current) / 1000;
    const duration = 0.4;

    if (elapsed > duration) {
      onComplete?.();
      return;
    }

    const progress = elapsed / duration;

    // Animate sparkles using instanced mesh
    if (sparkleInstanceRef.current) {
      sparkleData.forEach((data, i) => {
        const distance = data.speed * progress;
        const x = Math.cos(data.angle) * distance;
        const y = Math.sin(data.angle) * distance;
        const scale = data.size * (1 - progress);

        tempPosition.set(x, y, 0);
        tempScale.setScalar(scale);
        tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
        sparkleInstanceRef.current.setMatrixAt(i, tempMatrix);
      });
      sparkleInstanceRef.current.instanceMatrix.needsUpdate = true;
      sparkleInstanceRef.current.material.opacity = 1 - progress;
    }

    // Flash effect
    if (flashRef.current) {
      flashRef.current.material.opacity = (1 - progress) * 0.8;
      flashRef.current.scale.setScalar(1 + progress * 0.5);
    }
  });

  return (
    <group ref={groupRef} position={position}>
      {/* Central flash */}
      <mesh ref={flashRef} geometry={sharedPlaneGeometry}>
        <meshBasicMaterial
          color={baseColor}
          transparent
          opacity={0.8}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
        />
      </mesh>

      {/* Sparkles - Instanced for performance */}
      <instancedMesh
        ref={sparkleInstanceRef}
        args={[sharedCircleGeometry, sparkleMaterial, sparkleCount]}
        frustumCulled={false}
      />
    </group>
  );
};

export default BlockPlaceEffect;
